import { PermissionProviderType } from "models";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAttendDetail } from "./detail";

type EmailModel = {
  IdOmniEmail: number;
  IsFromMailing: boolean;
  NmSender: string;
  DeSender: string;
  DeRecipient: string;
  DeRecipientCc?: string;
  DeRecipientCco?: string;
  DeSubject: string;
  DtDisplay: string;
  DeEmail: string;
  DeEmailSummary: string;
};

type EmailContextType = {
  loading: boolean;
  emailSelect?: EmailModel;
  newEmail?: string;
  emails?: EmailModel[];
  getEmails: () => Promise<void>;
  showEmail: (email: EmailModel) => void;
  createEmail: (recipient: string) => void;
  closeEmail: () => void;
};

const staticList: EmailModel[] = [
  {
    IdOmniEmail: 1,
    IsFromMailing: true,
    NmSender: "Cliente 1",
    DeSender: "cliente@gmail.com",
    DeRecipient: "agente@gmail.com",
    DeSubject: "Sobre o módulo de e-mmail",
    DtDisplay: "2024-10-02 16:38",
    DeEmail:
      "<p>Aliquam magna est, tristique vitae aliquam sed, vehicula eu ligula. Vestibulum sed augue nec metus laoreet ultrices.</p>",
    DeEmailSummary:
      "Aliquam magna est, tristique vitae aliquam sed, vehicula eu ligula. Vestibulum sed augue nec metus laoreet ultrices.",
  },
  {
    IdOmniEmail: 2,
    IsFromMailing: false,
    NmSender: "Agente 1",
    DeSender: "agente@gmail.com",
    DeRecipient: "cliente@gmail.com",
    DeSubject: "Re: Sobre o módulo de e-mmail",
    DtDisplay: "2024-10-02 16:40",
    DeEmail:
      "<p>Nullam nec orci et lacus dignissim placerat sed id turpis. Cras mi lorem, tincidunt non augue ornare, volutpat malesuada neque.</p>",
    DeEmailSummary:
      "Nullam nec orci et lacus dignissim placerat sed id turpis. Cras mi lorem, tincidunt non augue ornare, volutpat malesuada neque.",
  },
  {
    IdOmniEmail: 3,
    IsFromMailing: true,
    NmSender: "Cliente 1",
    DeSender: "cliente@gmail.com",
    DeRecipient: "agente@gmail.com",
    DeSubject: "Atraso na entrega",
    DtDisplay: "2024-10-02 16:41",
    DeEmail:
      "<p>Duis faucibus nibh dui, sodales sagittis neque tincidunt et.</p>",
    DeEmailSummary:
      "Duis faucibus nibh dui, sodales sagittis neque tincidunt et.",
  },
  {
    IdOmniEmail: 4,
    IsFromMailing: false,
    NmSender: "Agente 1",
    DeSender: "agente@gmail.com",
    DeRecipient: "cliente@gmail.com",
    DeSubject: "Nova data de entrega",
    DtDisplay: "2024-10-02 16:45",
    DeEmail:
      "<p>Nam quis lectus mauris. Vivamus in faucibus lectus. Praesent tempus mauris mi, sed rhoncus ligula pretium et. Ut vitae cursus nunc.</p>",
    DeEmailSummary:
      "Nam quis lectus mauris. Vivamus in faucibus lectus. Praesent tempus mauris mi, sed rhoncus ligula pretium et. Ut vitae cursus nunc.",
  },
  {
    IdOmniEmail: 5,
    IsFromMailing: true,
    NmSender: "Cliente 1",
    DeSender: "cliente@gmail.com",
    DeRecipient: "agente@gmail.com",
    DeSubject: "Problema solucionado",
    DtDisplay: "2024-10-03 16:40",
    DeEmail:
      "<p>Vivamus vulputate elit lobortis lacus interdum, et feugiat tellus rutrum. Curabitur porta, diam quis aliquam venenatis, justo enim feugiat mi, aliquet interdum lorem diam ut elit. Vivamus pellentesque tortor nec sapien ullamcorper molestie.</p>",
    DeEmailSummary:
      "Vivamus vulputate elit lobortis lacus interdum, et feugiat tellus rutrum. Curabitur porta, diam quis aliquam venenatis, justo enim feugiat mi, aliquet interdum lorem diam ut elit. Vivamus pellentesque tortor nec sapien ullamcorper molestie.",
  },
];

type EmailProviderType = PermissionProviderType;

const EmailContext = createContext({} as EmailContextType);

const Provider = ({ children }: Omit<EmailProviderType, "hasPermission">) => {
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState<string>();
  const [emailSelect, setEmailSelect] = useState<EmailModel>();
  const [emails, setEmails] = useState<EmailModel[]>();
  const { pIdOmni } = useAttendDetail();

  useEffect(() => {
    if (pIdOmni) {
      setLoading(true);
      setNewEmail(undefined);
      setEmailSelect(undefined);
      setEmails(undefined);
    }
  }, [pIdOmni]);

  const getEmails = useCallback(async () => {
    setEmails(staticList);
    setLoading(false);
  }, []);

  const showEmail = useCallback(
    (email: EmailModel) => setEmailSelect(email),
    []
  );

  const createEmail = useCallback(
    (recipient: string) => setNewEmail(`${recipient};`),
    []
  );

  const closeEmail = useCallback(() => {
    setEmailSelect(undefined);
    setNewEmail(undefined);
  }, []);

  return (
    <EmailContext.Provider
      value={{
        loading,
        emails,
        emailSelect,
        newEmail,
        getEmails,
        showEmail,
        createEmail,
        closeEmail,
      }}
    >
      {children}
    </EmailContext.Provider>
  );
};

export const EmailProvider = ({
  hasPermission = true,
  ...args
}: EmailProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useEmail = () => useContext(EmailContext);
